export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/\D/g, '');

  if (cleaned.length === 0) return phoneNumber;

  if (cleaned.length >= 11) {
    let countryCode = '';
    let remaining = cleaned;

    if (cleaned.startsWith('1') && cleaned.length === 11) {
      countryCode = '1';
      remaining = cleaned.substring(1);
    } else if (cleaned.length >= 12) {
      countryCode = cleaned.substring(0, 2);
      remaining = cleaned.substring(2);
    } else if (cleaned.length === 11) {
      countryCode = cleaned.substring(0, 1);
      remaining = cleaned.substring(1);
    }

    if (countryCode && remaining.length >= 10) {
      const areaCode = remaining.substring(0, 3);
      const firstPart = remaining.substring(3, 7);
      const lastPart = remaining.substring(7, 11);
      return `+${countryCode}-${areaCode}-${firstPart}-${lastPart}`;
    }
  }

  if (cleaned.length === 10) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(
      3,
      6
    )}-${cleaned.substring(6)}`;
  }

  return phoneNumber;
};

/**
 * Normalizes a phone number for lookups and API calls.
 * Preserves a leading + when present and strips other formatting characters.
 */
export const normalizePhoneNumber = (phoneNumber?: string | null): string => {
  if (!phoneNumber) return '';

  const trimmed = phoneNumber.trim();
  if (!trimmed) return '';

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return '';

  if (trimmed.startsWith('+')) {
    return `+${digits}`;
  }

  if (trimmed.startsWith('00') && digits.length > 2) {
    return `+${digits.substring(2)}`;
  }

  return digits;
};

export const normalizePhoneNumbers = (
  phoneNumbers?: string | string[] | null
): string[] => {
  const values = Array.isArray(phoneNumbers) ? phoneNumbers : [phoneNumbers];

  return Array.from(
    new Set(values.map((value) => normalizePhoneNumber(value)).filter(Boolean))
  );
};

const getPhoneLookupVariants = (phoneNumber?: string | null): string[] => {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) return [];

  const digits = normalized.replace(/\D/g, '');
  const variants = new Set<string>([normalized, digits]);

  if (normalized.startsWith('+')) {
    variants.add(normalized.substring(1));
  }

  if (digits.length === 10) {
    variants.add(`1${digits}`);
    variants.add(`+1${digits}`);
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    variants.add(digits.substring(1));
    variants.add(`+${digits}`);
  }

  return Array.from(variants);
};

export const arePhoneNumbersEquivalent = (
  left?: string | null,
  right?: string | null
): boolean => {
  const leftVariants = getPhoneLookupVariants(left);
  const rightVariants = new Set(getPhoneLookupVariants(right));

  if (leftVariants.length === 0 || rightVariants.size === 0) {
    return false;
  }

  return leftVariants.some((variant) => rightVariants.has(variant));
};

export const formatPhoneNumberV2 = (phoneNumber: string): string => {
  if (!phoneNumber) return '';

  const cleaned = phoneNumber.replace(/\D/g, '');

  if (cleaned.length === 0) return phoneNumber;

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    const nationalNumber = cleaned.substring(1);
    return `${nationalNumber.substring(0, 3)}-${nationalNumber.substring(
      3,
      6
    )}-${nationalNumber.substring(6)}`;
  }

  return formatPhoneNumber(phoneNumber);
};
